import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, filter, map } from 'rxjs';
import { LocalizationActions } from 'src/app/modules/localization/store/actions';
import { LocalizationState } from 'src/app/modules/localization/store/state';
import { ApplicationPaths } from 'src/app/modules/login/api-authorization.constants';
import { AuthGreenService, AuthType } from 'src/app/modules/login/auth-green.service';
import { INavigationState } from 'src/app/modules/login/components/login.component';
import { PostUserDto, UserDto } from 'src/app/modules/login/services/user-api.service';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { selectInterfaceLanguages } from 'src/app/shared/store/selectors';
import { PermissionResource } from '../../constants/permissions.constants';
import { PermissionService } from '../../services/permission.service';
import { GetUserActions } from '../../store/actions';
import {
  selectAccessRestricted,
  selectAuthenticatedAccount,
  selectUser,
} from '../../store/selectors';
import { AppState } from '../../store/state';
import { isPresent } from '../../utils/isPresent';

interface LeftSideMenuItem {
  routerLink: string;
  labelKey: string;
  permissionResource?: PermissionResource;
  permissionResources?: PermissionResource[];
  icon?: string;
  cssClass?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-left-side-menu',
  templateUrl: './left-side-menu.component.html',
  styleUrl: './left-side-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeftSideMenuComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public isAuthenticatedAccount$ = this.store$.select(selectAuthenticatedAccount);
  public user: UserDto | undefined;
  public interfaceLanguages: LanguageDto[] = [];
  public isUser$ = this.store$.select(selectUser);
  public greenAuthUser: any;
  public availableLangs = this.translocoService.getAvailableLangs() as string[];
  public langBtnMenuItems: any[] = [];
  public accessRestricted: boolean = false;
  public visibleMainMenuItemsBeforeTeam$: Observable<LeftSideMenuItem[]>;
  public visibleMainMenuItemsAfterTeam$: Observable<LeftSideMenuItem[]>;
  public visibleTeamMenuItems$: Observable<LeftSideMenuItem[]>;

  private readonly mainMenuItemsBeforeTeam: LeftSideMenuItem[] = [
    {
      routerLink: '/documents',
      labelKey: 'menu.myDocuments',
      permissionResource: PermissionResource.Documents,
      icon: 'note_stack',
    },
    {
      routerLink: '/workspaces',
      labelKey: 'workspaces.myWorkspaces',
      permissionResource: PermissionResource.Workspaces,
      icon: 'workspaces',
    },
    {
      routerLink: '/playbooks',
      labelKey: 'menu.playbooks',
      permissionResource: PermissionResource.Playbooks,
      icon: 'menu_book',
    },
    {
      routerLink: '/clauses',
      labelKey: 'menu.clauseLibrary',
      permissionResource: PermissionResource.Clauses,
      icon: 'menu_book',
    },
    {
      routerLink: '/questions',
      labelKey: 'menu.keyQuestions',
      permissionResource: PermissionResource.Questions,
      icon: 'help',
    },
    {
      routerLink: '/document-types',
      labelKey: 'menu.docTypes',
      permissionResource: PermissionResource.DocumentTypes,
      icon: 'stacks',
    },
    {
      routerLink: '/counterparties',
      labelKey: 'menu.counterparties',
      permissionResource: PermissionResource.Counterparties,
      icon: 'view_cozy',
    },
  ];

  private readonly mainMenuItemsAfterTeam: LeftSideMenuItem[] = [
    {
      routerLink: '/metadata',
      labelKey: 'menu.metadata',
      permissionResource: PermissionResource.ExtractionMetadata,
      icon: 'tune',
    },
    {
      routerLink: '/baseline-documents',
      labelKey: 'menu.baselineDocuments',
      permissionResource: PermissionResource.BaselineDocuments,
      icon: 'cloud_upload',
    },
    {
      routerLink: '/profile',
      labelKey: 'menu.profile',
      icon: 'person',
    },
  ];

  private readonly teamMenuItems: LeftSideMenuItem[] = [
    {
      routerLink: '/team/users',
      labelKey: 'menu.users',
      permissionResources: [PermissionResource.Users, PermissionResource.AccessRoles],
      cssClass: 'team-menu__item',
    },
    {
      routerLink: '/team/groups',
      labelKey: 'menu.groups',
      permissionResource: PermissionResource.Groups,
      cssClass: 'team-menu__item',
    },
    {
      routerLink: '/team/team-roles',
      labelKey: 'menu.teamRoles',
      permissionResource: PermissionResource.TeamRoles,
      cssClass: 'team-menu__item',
    },
    {
      routerLink: '/team/access-roles',
      labelKey: 'menu.accessRoles',
      permissionResource: PermissionResource.AccessRoles,
      cssClass: 'team-menu__item',
    },
  ];

  constructor(
    public router: Router,
    private store$: Store<AppState>,
    private authService: MsalService,
    private authGreenService: AuthGreenService,
    public translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
    private localizationStore: Store<LocalizationState>,
    private permissionService: PermissionService,
  ) {
    this.visibleMainMenuItemsBeforeTeam$ = this.getVisibleMenuItems(this.mainMenuItemsBeforeTeam);
    this.visibleMainMenuItemsAfterTeam$ = this.getVisibleMenuItems(this.mainMenuItemsAfterTeam);
    this.visibleTeamMenuItems$ = this.getVisibleMenuItems(this.teamMenuItems);
  }

  ngOnInit(): void {
    combineLatest([this.store$.select(selectUser).pipe(untilDestroyed(this), filter(isPresent))])
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe(([user]) => {
        this.user = user;
        this.cdr.markForCheck();
      });

    this.translocoService.langChanges$
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe(() => {
        this.initLanguageSwitcher();
        this.cdr.markForCheck();
      });

    this.store$
      .select(selectInterfaceLanguages)
      .pipe(untilDestroyed(this))
      .subscribe((interfaceLanguages) => {
        this.interfaceLanguages = interfaceLanguages;
        this.initLanguageSwitcher();
        this.cdr.markForCheck();
      });

    this.store$
      .select(selectAccessRestricted)
      .pipe(untilDestroyed(this))
      .subscribe((accessRestricted) => {
        this.accessRestricted = accessRestricted;
        this.cdr.markForCheck();
      });
  }

  initLanguageSwitcher(): void {
    this.langBtnMenuItems = this.availableLangs.map((lang) => ({
      label: this.translocoService.translate('language.' + lang),
      icon: `flag-option flag-${lang}`,
      command: () => this.changeLang(lang),
    }));
  }

  onMenuClick(step: number): void {
    this.router.navigate(['/documents'], { replaceUrl: false, skipLocationChange: false });
  }

  logout(popup?: boolean) {
    const authType = localStorage.getItem('AUTH_TYPE') || '';

    if (+authType === AuthType.EMAIL) {
      const state: INavigationState = { returnUrl: ApplicationPaths.LoggedOut };
      this.authGreenService.signOut(state);
    } else {
      if (popup) {
        this.authService.logoutPopup({
          mainWindowRedirectUri: '/login',
        });
      } else {
        this.authService.logoutRedirect();
      }
    }
  }

  changeLang(selectedLangName: any) {
    this.localizationStore.dispatch(LocalizationActions.setLang({ lang: selectedLangName }));

    const selectedLang = this.interfaceLanguages.find((lang: LanguageDto) => {
      return lang.name === selectedLangName;
    });

    if (!this.user) {
      this.cdr.markForCheck();
      return;
    }

    if (selectedLang) {
      const user = { ...this.user } as PostUserDto;
      user.defaultInterfaceLanguageId = selectedLang.id;
      this.store$.dispatch(GetUserActions.saveUserDetails({ user }));
      this.cdr.markForCheck();
    }
  }

  get activeLang() {
    return this.translocoService.getActiveLang();
  }

  private getVisibleMenuItems(items: LeftSideMenuItem[]): Observable<LeftSideMenuItem[]> {
    return this.permissionService.getCurrentPermissions$().pipe(
      map((permissions) =>
        items.filter((item) => {
          if (item.permissionResources?.length) {
            return item.permissionResources.every((resource) =>
              this.permissionService.canReadResource(resource, permissions),
            );
          }

          return (
            !item.permissionResource ||
            this.permissionService.canReadResource(item.permissionResource, permissions)
          );
        }),
      ),
    );
  }
}
